import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface ArticleInventaire {
  id: string;
  user_id: string;
  bateau_id: string | null;
  nom: string;
  description: string | null;
  notes: string | null;
  categorie: string;
  sous_categorie: string | null;
  quantite: number;
  quantite_max: number;
  unite: string;
  prix_unitaire: number | null;
  date_achat: string | null;
  date_peremption: string | null;
  espace_stockage_id: string | null;
  pourcentage: number;
  created_at: string;
  updated_at: string;
}

export interface Categorie {
  id: string;
  nom: string;
  icone?: string;
}

export interface SousCategorie {
  id: string;
  nom: string;
  categorie_id: string;
}

export const useInventaire = () => {
  const [articles, setArticles] = useState<ArticleInventaire[]>([]);
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [sousCategories, setSousCategories] = useState<SousCategorie[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchArticles = useCallback(async (bateauId?: string) => {
    if (!user) {
      setArticles([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    console.log('Fetching articles for user:', user.id, 'boat:', bateauId);
    
    try {
      let query = supabase
        .from('inventaire')
        .select('*')
        .eq('user_id', user.id)
        .order('nom');

      if (bateauId) {
        query = query.eq('bateau_id', bateauId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erreur lors du chargement des articles:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les articles de l'inventaire.",
          variant: "destructive",
        });
        setArticles([]);
      } else {
        const articlesTyped: ArticleInventaire[] = (data || []).map(item => ({
          ...item,
          pourcentage: item.quantite_max > 0 ? (item.quantite / item.quantite_max) * 100 : 0,
          unite: item.unite || 'unité' // Fallback pour les anciens articles
        }));
        setArticles(articlesTyped);
        console.log('Articles loaded:', articlesTyped.length);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchCategories = useCallback(async () => {
    if (!user) {
      setCategories([]);
      return;
    }

    try {
      // Récupérer les catégories de l'utilisateur ET les catégories partagées (user_id = null)
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('nom');

      if (error) {
        console.error('Erreur lors du chargement des catégories:', error);
        setCategories([]);
      } else {
        setCategories(data || []);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setCategories([]);
    }
  }, [user]);

  const fetchSousCategories = useCallback(async () => {
    if (!user) {
      setSousCategories([]);
      return;
    }

    try {
      // Récupérer les sous-catégories de l'utilisateur ET les sous-catégories partagées (user_id = null)
      const { data, error } = await supabase
        .from('sous_categories')
        .select('*')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('nom');

      if (error) {
        console.error('Erreur lors du chargement des sous-catégories:', error);
        setSousCategories([]);
      } else {
        setSousCategories(data || []);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setSousCategories([]);
    }
  }, [user]);

  const ajouterArticle = async (articleData: Omit<ArticleInventaire, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'pourcentage'>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('inventaire')
        .insert({
          user_id: user.id,
          bateau_id: articleData.bateau_id,
          nom: articleData.nom,
          description: articleData.description,
          notes: articleData.notes,
          categorie: articleData.categorie,
          sous_categorie: articleData.sous_categorie,
          quantite: articleData.quantite,
          quantite_max: articleData.quantite_max,
          unite: articleData.unite || 'unité',
          prix_unitaire: articleData.prix_unitaire,
          date_achat: articleData.date_achat,
          date_peremption: articleData.date_peremption,
          espace_stockage_id: articleData.espace_stockage_id
        })
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout de l\'article:', error);
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter l'article.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Article ajouté avec succès !",
        });
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const ajouterPlusieursArticles = async (articlesData: any[], bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    console.log('Ajout de plusieurs articles:', articlesData.length);

    try {
      const articlesFormated = articlesData.map(article => ({
        user_id: user.id,
        bateau_id: bateauId,
        nom: article.nom,
        description: article.description || null,
        categorie: article.categorie,
        sous_categorie: article.sous_categorie,
        quantite: article.quantite || 0,
        quantite_max: article.quantite_max || 1,
        unite: article.unite || 'unité',
        prix_unitaire: article.prix_unitaire || null,
        date_achat: article.date_achat,
        date_peremption: article.date_peremption,
        espace_stockage_id: article.espace_stockage_id
      }));

      const { data, error } = await supabase
        .from('inventaire')
        .insert(articlesFormated)
        .select();

      if (error) {
        console.error('Erreur lors de l\'ajout des articles:', error);
        return { error };
      } else {
        console.log('Articles ajoutés avec succès:', data?.length || 0);
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierArticle = async (id: string, updates: Partial<ArticleInventaire>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    console.log('=== DÉBUT modifierArticle ===');
    console.log('Modification article - données reçues:', updates);
    console.log('ID article:', id);

    try {
      // Préparer les données en s'assurant que les champs optionnels sont null et pas une chaîne vide
      const updateData = {
        nom: updates.nom,
        description: updates.description || null,
        notes: updates.notes || null,
        categorie: updates.categorie,
        sous_categorie: updates.sous_categorie || null,
        quantite: updates.quantite,
        quantite_max: updates.quantite_max,
        unite: updates.unite || 'unité',
        prix_unitaire: updates.prix_unitaire || null,
        date_achat: updates.date_achat || null,
        date_peremption: updates.date_peremption || null,
        espace_stockage_id: updates.espace_stockage_id || null
      };

      console.log('Données finales envoyées à Supabase:', updateData);

      const { data, error } = await supabase
        .from('inventaire')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification Supabase:', error);
        toast({
          title: "Erreur",
          description: "Impossible de modifier l'article.",
          variant: "destructive",
        });
        return { error };
      } else {
        console.log('Article modifié avec succès dans Supabase:', data);
        
        // Mettre à jour immédiatement l'état local
        setArticles(prevArticles => 
          prevArticles.map(article => 
            article.id === id ? { ...article, ...data } : article
          )
        );
        
        console.log('État local mis à jour');
        
        toast({
          title: "Succès",
          description: "Article modifié avec succès !",
        });
        console.log('=== FIN modifierArticle ===');
        return { data };
      }
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      return { error: err };
    }
  };

  const supprimerArticle = async (id: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('inventaire')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer l'article.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Article supprimé avec succès !",
        });
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const supprimerTousArticles = async (articleIds: string[], bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('inventaire')
        .delete()
        .in('id', articleIds)
        .eq('user_id', user.id)
        .eq('bateau_id', bateauId);

      if (error) {
        console.error('Erreur lors de la suppression en lot:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer les articles.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: `${articleIds.length} articles supprimés avec succès !`,
        });
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const reinitialiserTousLesStocks = async (articleIds: string[], bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('inventaire')
        .update({ quantite: 0 })
        .in('id', articleIds)
        .eq('user_id', user.id)
        .eq('bateau_id', bateauId);

      if (error) {
        console.error('Erreur lors de la réinitialisation:', error);
        toast({
          title: "Erreur",
          description: "Impossible de réinitialiser les stocks.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: `${articleIds.length} stocks réinitialisés avec succès !`,
        });
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const remplirTousLesStocks = async (articleIds: string[], bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      // Récupérer les articles pour connaître leur quantite_max
      const { data: articles, error: fetchError } = await supabase
        .from('inventaire')
        .select('id, quantite_max')
        .in('id', articleIds)
        .eq('user_id', user.id)
        .eq('bateau_id', bateauId);

      if (fetchError) {
        console.error('Erreur lors de la récupération des articles:', fetchError);
        return { error: fetchError };
      }

      // Mettre à jour chaque article individuellement
      if (articles && articles.length > 0) {
        for (const article of articles) {
          await supabase
            .from('inventaire')
            .update({ quantite: article.quantite_max })
            .eq('id', article.id)
            .eq('user_id', user.id);
        }
      }

      toast({
        title: "Succès",
        description: `${articleIds.length} stocks remplis à 100% !`,
      });
      return { success: true };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierQuantite = async (articleId: string, nouvelleQuantite: number, bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('inventaire')
        .update({ quantite: nouvelleQuantite })
        .eq('id', articleId)
        .eq('user_id', user.id)
        .eq('bateau_id', bateauId);

      if (error) {
        console.error('Erreur lors de la modification de quantité:', error);
        return { error };
      } else {
        // Mettre à jour l'état local sans refetch pour éviter de fermer les catégories
        setArticles(prevArticles => 
          prevArticles.map(article => 
            article.id === articleId 
              ? { 
                  ...article, 
                  quantite: nouvelleQuantite,
                  pourcentage: article.quantite_max > 0 ? (nouvelleQuantite / article.quantite_max) * 100 : 0
                } 
              : article
          )
        );
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const ajouterCategorie = async (nomCategorie: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('categories')
        .insert({ 
          nom: nomCategorie,
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout de la catégorie:', error);
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter la catégorie.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Catégorie ajoutée avec succès !",
        });
        // Rafraîchir les catégories
        await fetchCategories();
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const refetchArticles = async (bateauId?: string) => {
    await fetchArticles(bateauId);
  };

  useEffect(() => {
    fetchCategories();
    fetchSousCategories();
  }, [fetchCategories, fetchSousCategories]);

  useEffect(() => {
    if (user) {
      fetchArticles();
    }
  }, [user, fetchArticles]);

  return {
    articles,
    categories,
    sousCategories,
    loading,
    ajouterArticle,
    ajouterPlusieursArticles,
    modifierArticle,
    supprimerArticle,
    supprimerTousArticles,
    reinitialiserTousLesStocks,
    remplirTousLesStocks,
    modifierQuantite,
    ajouterCategorie,
    refetchArticles,
    fetchCategories,
    fetchSousCategories
  };
};
